import React from 'react';

type NotificationType = 'success' | 'error' | 'warning' | 'info';

interface NotificationModalProps {
  isOpen: boolean;
  type: NotificationType;
  title: string;
  message: string;
  onClose: () => void;
}

export const NotificationModal: React.FC<NotificationModalProps> = ({
  isOpen,
  type,
  title,
  message,
  onClose,
}) => {
  if (!isOpen) return null;

  const getIconAndColor = () => {
    switch (type) {
      case 'success':
        return {
          bg: 'bg-green-500/20',
          icon: (
            <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
            </svg>
          ),
        };
      case 'error':
        return {
          bg: 'bg-red-500/20',
          icon: (
            <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          ),
        };
      case 'warning':
        return {
          bg: 'bg-yellow-500/20',
          icon: (
            <svg className="w-8 h-8 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
            </svg>
          ),
        };
      case 'info':
      default:
        return {
          bg: 'bg-blue-500/20',
          icon: (
            <svg className="w-8 h-8 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
          ),
        };
    }
  };

  const { bg, icon } = getIconAndColor();

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm" 
      role="dialog" 
      aria-modal="true" 
      aria-labelledby="notification-dialog-title"
      onClick={onClose}
    >
      <div 
        className="glass-effect border border-primary/20 rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`flex items-center justify-center w-16 h-16 ${bg} rounded-full mb-6 mx-auto`}>
          {icon}
        </div>
        
        <h2 id="notification-dialog-title" className="text-2xl font-bold text-gradient-primary mb-3 text-center">
          {title}
        </h2>
        <p className="text-gray-400 mb-6 text-center whitespace-pre-line">
          {message}
        </p>

        <button
          onClick={onClose}
          className="w-full px-6 py-3 bg-primary hover:bg-primary-light text-dark font-bold rounded-xl transition-all duration-200 shadow-lg shadow-primary/20"
        >
          Close
        </button>
      </div>
    </div>
  );
};
